<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import ContentStatsDebug from './components/debug/ContentStatsDebug.vue';
import LoadingApp from './components/LoadingApp.vue';
import SiteUnderConstruction from './components/SiteUnderConstruction.vue';
import PwaRefreshPrompt from './components/PwaRefreshPrompt.vue';
import Navigation from './components/Navigation.vue';
import SiteFooter from './components/SiteFooter.vue';
import WagtailUserbar from './components/WagtailUserbar.vue';
import { isBlockPreviewPathname } from './blockPreviewPath';
import { useAuthStore } from './stores/auth';
import { useContentStore } from './stores/content';
import { useCanonicalLink } from './composables/useCanonicalLink';

useCanonicalLink();

const authStore = useAuthStore();
const contentStore = useContentStore();
const route = useRoute();

/**
 * Wagtail block-picker iframe: use pathname first so the first paint matches router meta
 * (before Vue Router resolves), avoiding a flash of nav/loading.
 */
const isBlockPreview = computed(
  () =>
    (typeof window !== 'undefined' && isBlockPreviewPathname(window.location.pathname)) ||
    Boolean(route.meta.blockPreview)
);

/** Wagtail draft/live preview (iframe or shared preview URL). User bar must stay off in this shell. */
const isWagtailContentPreview = computed(
  () =>
    contentStore.isCurrentPreviewMode ||
    (typeof route.query.content_type === 'string' &&
      typeof route.query.token === 'string'),
);

const contentReady = computed(() => contentStore.status === 'ready');
/** Account routes work without CMS listing/root so staff can sign in while the site is empty. */
const isAccountRoute = computed(() => route.path.startsWith('/account'));
/** Employer certificate check: no full CMS bootstrap required. */
const isCertificateVerifyRoute = computed(() => route.path.startsWith('/certificate/verify'));

/**
 * When the public site cannot bootstrap (no home in API, etc.), show a gate unless the user
 * is on /account (login still works).
 */
const bootstrapGatePhase = computed((): 'none' | 'loading' | 'construction' | 'error' => {
  if (contentReady.value || isAccountRoute.value || isCertificateVerifyRoute.value) return 'none';
  if (contentStore.status === 'loading' || contentStore.status === 'uninitialized') return 'loading';
  if (contentStore.siteBootstrapError) return 'construction';
  if (contentStore.status === 'error') return 'error';
  return 'loading';
});

const showMainChrome = computed(
  () => contentReady.value || isAccountRoute.value || isCertificateVerifyRoute.value,
);

/** Wagtail page pk for the user bar (edit / explore / add child); null when no page is loaded. */
const userbarPageId = computed((): number | null => {
  const payload = contentStore.getCurrentPagePayload;
  if (payload && typeof payload.id === 'number') return payload.id;
  return null;
});

/**
 * Wagtail's userbar runs axe once when `<wagtail-userbar>` connects (see `onWindowLoad` in
 * `wagtailadmin/js/userbar.js`). If we inject it before the preview page payload has rendered
 * into the DOM, axe scans an empty outline and never re-runs — Checks then wrongly show 0 issues.
 */
const wagtailUserbarInjectAllowed = computed(() => {
  if (!isWagtailContentPreview.value) return true;
  const p = contentStore.currentPreview;
  return (
    contentStore.status === 'ready' &&
    p.isActive &&
    !p.loading &&
    p.payload != null &&
    !p.error
  );
});

/** Only staff with Wagtail admin access see the userbar; preview iframe still mounts it for axe. */
const wagtailUserbarVisible = computed(
  () =>
    isWagtailContentPreview.value ||
    (authStore.isAuthenticated && authStore.currentUser?.has_wagtail_admin_access === true),
);
</script>

<template>
  <div class="app-layout" :class="{ 'app-layout--block-preview': isBlockPreview }">
    <template v-if="isBlockPreview">
      <main class="app-main app-main--block-preview">
        <RouterView />
      </main>
    </template>
    <template v-else>
      <Navigation v-if="contentReady && contentStore.data" />
      <LoadingApp
        v-if="bootstrapGatePhase === 'loading' || bootstrapGatePhase === 'error'"
        :error="bootstrapGatePhase === 'error'"
        :message="bootstrapGatePhase === 'error' ? (contentStore.error ?? 'Something went wrong') : undefined"
      />
      <SiteUnderConstruction v-else-if="bootstrapGatePhase === 'construction'" />
      <main
        v-else-if="showMainChrome"
        class="app-main"
        :class="{
          'app-main--bootstrap-account':
            !contentReady && (isAccountRoute || isCertificateVerifyRoute),
        }"
      >
        <div
          class="app-main-inner"
          :class="{
            'app-main-inner--bootstrap-account':
              !contentReady && (isAccountRoute || isCertificateVerifyRoute),
          }"
        >
          <RouterView />
          <ContentStatsDebug v-if="false"/>
        </div>
      </main>
      <SiteFooter v-if="contentReady && contentStore.data" />
    </template>
  </div>
  <!--
    In Wagtail's preview iframe we still mount the userbar so the in-shadow
    accessibility checker can run and post results back to the admin "Checks" panel
    via `w-userbar:axe-ready`. The visible bar stays hidden because the backend
    renders `<aside hidden>` when `in_preview_panel=1`.
  -->
  <WagtailUserbar
    v-if="!isBlockPreview"
    :hidden="!wagtailUserbarVisible"
    :page-id="userbarPageId"
    :in-preview-panel="isWagtailContentPreview"
    :inject-allowed="wagtailUserbarInjectAllowed"
  />
  <PwaRefreshPrompt v-if="false"/>
</template>

<style scoped>
.app-layout--block-preview {
  min-height: 100vh;
  background-color: #fafafa;
}

.app-layout {
  display: flex;
  width: 100%;
  min-height: 100vh;
  flex-direction: column;
  background-color: #fafafa;
  /* Clears fixed headers in Navigation — match prototype nav bar (~100px) */
  --site-header-height: 100px;
}
.app-main {
  flex: 1;
  width: 100%;
  min-width: 0;
  /*
   * Do not use overflow: auto here: it establishes a scroll container while the page
   * usually scrolls on the window. Sticky (e.g. section SidebarNav) then resolves
   * against that non-scrolling box and scrolls away. Match prototype: document scroll
   * + overflow visible on main.
   */
  overflow: visible;
  background-color: #fafafa;
}
/* Match Prototype Layout main: pt-[100px] px-4 sm:px-6 lg:px-8 + max-w-7xl alignment with HorizontalNav */
.app-main-inner {
  width: 100%;
  max-width: 80rem;
  margin-left: auto;
  margin-right: auto;
  padding-top: var(--site-header-height);
  padding-left: 1rem;
  padding-right: 1rem;
  /* Space before SiteFooter; match PageView header rhythm. Home + partners: zero via :has() below. */
  padding-bottom: 2rem;
  position: relative;
  box-sizing: border-box;
}
@media (min-width: 640px) {
  .app-main-inner {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-top: var(--site-header-height);
  }
}
@media (min-width: 1024px) {
  .app-main-inner {
    padding-left: 2rem;
    padding-right: 2rem;
    padding-top: var(--site-header-height);
    padding-bottom: 3rem;
  }
}

.app-main-inner:has(.partners-carousel__breakout) {
  padding-bottom: 0;
}

/* No site nav while CMS is still bootstrapping — keep account usable without a large header gap */
.app-main--bootstrap-account {
  --site-header-height: 1.5rem;
}
.app-main-inner--bootstrap-account {
  padding-top: var(--site-header-height);
}
</style>
